import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useModal } from "context";

// Components
import CloseButton from "reusableComponents/CloseButton";
import DashboardMenu from "layouts/DashboardLayout/DashboardMenu";
import LanguageDropdown from "../LanguageDropdown";
import HeaderSearchForm from "../HeaderSearchForm";

// Icons & Images
import { KInvertedLogoSvg } from "assets/icons/logos/logosInSvg";
import { LogoHeader } from "assets/icons/logos";
import loginIcon from "assets/icons/loginIcon.svg";
import basketIcon from "assets/images/003-panier_burger_menu.svg";
import inscriptionIcon from "assets/images/001-inscription_burger_menu.svg";
import entrustUsIcon from "assets/images/004-confiez-nous_burger_menu.svg";
import listSpaceIcon from "assets/images/005-referencer_burger_menu.svg";
import whoAreWeIcon from "assets/images/006-quisommes-nous_burger_menu.svg";
import FAQIcon from "assets/images/007-FAQ_burger_menu.svg";
import phoneIcon from "assets/images/phone-call-(1).svg";
import profileIcon from "assets/images/002-monCompte_burger_menu.svg";

// Selectors
import { selectIsAuthenticated, selectUserType } from "store/auth/selectors";

// Styles
import styles from "./styles.module.scss";
import { Login, SignupForm } from "modals";
import SearchInput from "../PlaceSearchForm";
import CTAButton from "../CTAButton";
import { CloseIcon } from "modules/Header/headerSvgIcons";

const Sidebar = ({ closeSidebar, isOpen }) => {
  const { t } = useTranslation("global");
  const { openModal } = useModal();
  const navigate = useNavigate();

  // Selectors
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userType = useSelector(selectUserType);
  const currentUser = useSelector((state) => state.auth.currentUser);

  // Phone click handler with tracking - using 'header' as call_source
  const handlePhoneClick = (e) => {
    e.preventDefault();
    
    // Push click event
    window.dataLayer.push({
      event: 'clic_appel_telephone_menu_mobile',
      call_source: 'mobile_menu'
    });

    // Set up focus lost tracking
    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.dataLayer.push({
          event: 'call_button_focus_lost',
          call_source: 'mobile_menu'
        });
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });
    
    // Open phone dialer
    window.location.href = "tel:+33180522522";
  };

  const handleNavigation = (path) => {
    navigate(path);
    closeSidebar();
  };

  const handleAuthAction = (type) => {
    openModal({
      content: <SignupForm type={type} />,
      title: t(
        `modal.register.title${type.charAt(0).toUpperCase() + type.slice(1)}`
      ),
    });
    closeSidebar();
  };

  const menuItems = [
    !isAuthenticated && {
      icon: inscriptionIcon,
      text: t("navbar.register"),
      action: () => handleAuthAction("seeker"),
      className: styles.sidebarMenuItemRegister,
    },
    !isAuthenticated && {
      icon: loginIcon,
      text: t("navbar.login"),
      action: () =>
        openModal({ content: <Login       redirectToDashboard={true}  // ⭐ ADD THIS LINE
/>, title: t("modal.login.title") }),
      className: styles.sidebarMenuItemLogin,
    },
    isAuthenticated && {
      icon: profileIcon,
      text: `${currentUser?.first_name || ""} ${
        currentUser?.last_name || ""
      }`.trim(),
      action: () => handleNavigation("/dashboard/home"),
      className: styles.sidebarMenuItemProfile,
    },
    userType !== "provider" && {
      icon: basketIcon,
      text: t("mobilemenu.basket"),
      action: () => handleNavigation("/basket"),
      className: styles.sidebarMenuItemBasket,
    },
    !isAuthenticated && {
      icon: entrustUsIcon,
      text: t("navbar.entrust_research"),
      action: () => handleNavigation("/contact-organiser-evenement"),
      className: styles.sidebarMenuItemEntrust,
    },
    !isAuthenticated && {
      icon: listSpaceIcon,
      text: t("navbar.rentASpace"),
      action: () => handleAuthAction("provider"),
      className: styles.sidebarMenuItemRentSpace,
    },
    !isAuthenticated && {
      icon: whoAreWeIcon,
      text: t("navbar.who_we_are"),
      action: () => handleNavigation("/qui-sommes-nous"),
      className: styles.sidebarMenuItemAbout,
    },
    !isAuthenticated && {
      icon: FAQIcon,
      text: t("footer.faq"),
      action: () => handleNavigation("/faq"),
      className: styles.sidebarMenuItemFaq,
    },
  ].filter(Boolean);

  return (
    <div
      className={`${styles.sidebarContainer} ${
        isOpen ? styles.sidebarOpen : styles.sidebarClosed
      }`}
    >
      <div className={styles.sidebarHeader}>
        <img
          className={styles.sidebarLogo}
          src={LogoHeader}
          alt="Company Logo"
          onClick={() => handleNavigation("/")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleNavigation("/")}
        />
        <CTAButton
          variant="icon"
          onClick={closeSidebar}
          className={styles.sidebarCloseButton}
        >
          <CloseIcon />
        </CTAButton>
      </div>

      <div className={styles.sidebarContent}>
        <div className={styles.sidebarSearchContainer}>
          <SearchInput onSearch={closeSidebar}/>
        </div>

        {/* Minimalistic Phone Button - clean and subtle */}
        <button
          className={styles.sidebarPhoneButton}
          onClick={handlePhoneClick}
          data-testid="call-button"
          aria-label={t("ariaLabels.callUs")}
        >
          <img
            src={phoneIcon}
            className={styles.sidebarPhoneIcon}
            alt=""
            aria-hidden="true"
          />
          <span className={styles.sidebarPhoneText}>+33 (0) 1 80 522 522</span>
        </button>

        <nav className={styles.sidebarMenu}>
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={item.action}
              className={`${styles.sidebarMenuItem} ${item.className || ""}`}
            >
              <img
                src={item.icon}
                className={styles.sidebarMenuItemIcon}
                alt=""
                aria-hidden="true"
              />
              <span className={styles.sidebarMenuItemText}>{item.text}</span>
            </button>
          ))}

          {isAuthenticated && (
            <div className={styles.sidebarDashboardMenu}>
              <DashboardMenu closeSidebar={closeSidebar} isMobileView={true} />
            </div>
          )}
          <div className={styles.sidebarLanguageSelector}>
            <LanguageDropdown />
          </div>
        </nav>
        <footer className={styles.sidebarFooter}>
          <div className={styles.sidebarFooterItem}>
            <span className={styles.sidebarFooterText}>Choose and Work</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Sidebar;